package com.essntl.features.client.ui.addeditclient

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AddEditClientViewModel(
    private val clientRepository: ClientRepository,
    private val proposalRepository: ProposalRepository,
    private val itineraryRepository: ItineraryRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditClientState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    fun onEvent(event: AddEditClientEvent) {
        when (event) {
            is AddEditClientEvent.GetClient -> getClient(event)
            is AddEditClientEvent.GetClientByPhoneNumber -> getClientByPhoneNumber(event)
            is AddEditClientEvent.UpdateClientFields -> updateClientFields(event)
            is AddEditClientEvent.AddClient -> addClient()
            is AddEditClientEvent.EditClient -> editClient()
            is AddEditClientEvent.ClearClientState -> clearClientState()
            is AddEditClientEvent.ClearSuccessState -> clearSuccessState()
        }
    }

    private var getClientJob: Job? = null
    private fun getClient(event: AddEditClientEvent.GetClient) {
        if (state.value.getClient.data?.id == event.clientId) return

        state.value.client.value = ClientModel.empty.copy(id = event.clientId)

        // Make sure to cancel the previous job
        // The user can switch between ta quickly before the job is finished,
        // In that case we don't want to show the result of the previous job
        getClientJob?.cancel()
        getClientJob = getData(
            getData = { clientRepository.getById(event.clientId) },
            getState = { state.value.getClient },
            setState = {
                state.value = state.value.copy(
                    getClient = it,
                    isEditEnabled = false,
                )
            },
            onSuccess = { client ->
                state.value.client.value = client
                getProposals(client.id)
            },
        )
    }

    private fun getClientByPhoneNumber(event: AddEditClientEvent.GetClientByPhoneNumber) {
        if (state.value.getClient.isLoading) return

        state.update {
            it.copy(
                getClient = State.loading(),
            )
        }

        scope.launch {
            loadingManager.emitLoading(true)
            clientRepository.getByPhoneNumbers(setOf(event.phoneNumber))
                .onSuccess { c ->
                    val client = c.firstOrNull()
                    if (client == null)
                        state.update {
                            it.copy(
                                getClient = State(data = null),
                            )
                        }
                    else {
                        state.update {
                            it.copy(
                                getClient = State.success(client),
                            )
                        }

                        state.value.client.value = client
                        getProposals(client.id)
                    }
                }
                .onFailure {
                    state.update {
                        it.copy(
                            getClient = State.failure(),
                        )
                    }
                }
        }.invokeOnCompletion {
            scope.launch {
                loadingManager.emitLoading(false)
            }
        }
    }


    private fun getProposals(clientId: String) =
        getData(
            getData = { proposalRepository.getProposalsByClientId(clientId) },
            getState = { state.value.getProposals },
            setState = { state.value = state.value.copy(getProposals = it) },
            onSuccess = { proposals ->
                if (proposals.isNotEmpty()) {
                    getItineraries(proposals.map { proposal -> proposal.id })
                }
            },
        )

    private fun getItineraries(proposalIds: List<String>) =
        getData(
            getData = { itineraryRepository.getItinerariesByProposalIds(proposalIds) },
            getState = { state.value.getItineraries },
            setState = { state.value = state.value.copy(getItineraries = it) },
        )

    private fun updateClientFields(event: AddEditClientEvent.UpdateClientFields) {
        state.value.client.value = event.client
        val newClient = state.value.getClient.data?.getChangedFields(state.value.client.value) ?: return
        state.update {
            it.copy(isEditEnabled = !newClient.isEmpty)
        }
    }

    private fun addClient() =
        getData(
            getData = { clientRepository.insert(state.value.client.value) },
            getState = { state.value.addEditClient },
            setState = { state.value = state.value.copy(addEditClient = it) },
            onSuccess = { client ->
                state.update {
                    it.copy(
                        getClient = State.success(client),
                    )
                }
                state.value.client.value = client

                toastManager.success(
                    description = "Client created successfully",
                )
            },
        )

    private fun editClient() {
        val newClient = state.value.getClient.data?.getChangedFields(state.value.client.value) ?: return

        if (newClient.isEmpty) return

        getData(
            getData = {
                clientRepository.update(
                    newClient.copy(
                        firstName = state.value.client.value.firstName,
                        lastName = state.value.client.value.lastName,
                        travelAgent = state.value.client.value.travelAgent,
                    ),
                )
            },
            getState = { state.value.addEditClient },
            setState = { state.value = state.value.copy(addEditClient = it) },
            onSuccess = { client ->
                state.update {
                    it.copy(
                        getClient = State.success(client),
                    )
                }
                state.value.client.value = client

                toastManager.success(
                    description = "Client updated successfully",
                )
            },
        )
    }

    private fun clearSuccessState() {
        state.value = state.value.copy(
            addEditClient = State(),
            isEditEnabled = false,
        )
    }

    private fun clearClientState() {
        state.value = state.value.copy(
            getClient = State(),
            addEditClient = State(),
            isEditEnabled = false,
            getItineraries = State(),
            getProposals = State(),
        )
        state.value.client.value = ClientModel.empty
    }
}